/* MEXC's spot wallet, read with the same key as the futures one.
 *
 * Spot is a separate part of MEXC with its own API and its own way of signing:
 * the query string alone is signed with the secret, the time goes in as a
 * parameter, and the key rides in an X-MEXC-APIKEY header. Balances come back
 * with what is free and what is locked in open orders. Orders are listed per
 * trading pair — MEXC will not list them all at once — so they are asked for
 * the pairs of the coins actually held. */

import { brokerGet } from './brokerLink';
import { MexcRefused, type MexcKeys } from './mexc';

const SPOT = 'https://api.mexc.com';

export interface SpotBalance {
  asset: string;
  free: number;
  locked: number;
}

export interface SpotOrder {
  symbol: string;
  side: 'buy' | 'sell';
  price: number;
  quantity: number;
  filled: number;
  at: string;
}

function toHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** HMAC-SHA256 of the query string, as lowercase hex — what spot calls the
    signature. From WebCrypto, like the futures one; no dependency (§2). */
export async function signQuery(secret: string, query: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'],
  );
  return toHex(await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(query)));
}

/** The parameters in the order given, exactly as they are sent and signed. */
export function queryString(params: Record<string, string | number>): string {
  return Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

function parse(body: string): unknown {
  let data: unknown;
  try {
    data = JSON.parse(body);
  } catch {
    throw new Error('MEXC spot answered with something that was not JSON.');
  }
  const refusal = data as { code?: unknown; msg?: unknown } | null;
  if (refusal && typeof refusal === 'object' && !Array.isArray(refusal)
    && typeof refusal.code === 'number' && refusal.code !== 0 && refusal.code !== 200) {
    throw new MexcRefused(refusal.code, typeof refusal.msg === 'string' ? refusal.msg : 'MEXC spot refused the request.');
  }
  return data;
}

async function spotGet(
  keys: MexcKeys, path: string, params: Record<string, string | number>, signal?: AbortSignal,
): Promise<unknown> {
  const query = queryString({ ...params, recvWindow: 10000, timestamp: Date.now() });
  const signature = await signQuery(keys.secret, query);
  return parse(await brokerGet({
    url: `${SPOT}${path}?${query}&signature=${signature}`,
    headers: { 'X-MEXC-APIKEY': keys.apiKey, 'Content-Type': 'application/json' },
    ...(signal ? { signal } : {}),
  }));
}

const num = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN);

/** Every asset in the spot wallet with anything in it, free or locked. */
export async function fetchSpotBalances(keys: MexcKeys, signal?: AbortSignal): Promise<SpotBalance[]> {
  const data = await spotGet(keys, '/api/v3/account', {}, signal) as { balances?: unknown };
  const rows = Array.isArray(data?.balances) ? data.balances as Array<Record<string, unknown>> : [];
  return rows
    .map((r) => ({ asset: String(r['asset'] ?? ''), free: num(r['free']), locked: num(r['locked']) }))
    .filter((b) => b.asset !== '' && Number.isFinite(b.free) && Number.isFinite(b.locked));
}

/** Open orders on one trading pair, such as BTCUSDT. */
export async function fetchSpotOpenOrders(keys: MexcKeys, pair: string, signal?: AbortSignal): Promise<SpotOrder[]> {
  const data = await spotGet(keys, '/api/v3/openOrders', { symbol: pair }, signal);
  if (!Array.isArray(data)) return [];
  return (data as Array<Record<string, unknown>>).map((o) => ({
    symbol: String(o['symbol'] ?? pair),
    side: String(o['side'] ?? '').toUpperCase() === 'SELL' ? 'sell' as const : 'buy' as const,
    price: num(o['price']),
    quantity: num(o['origQty']),
    filled: num(o['executedQty']),
    at: new Date(num(o['time']) || Date.now()).toISOString(),
  })).filter((o) => Number.isFinite(o.price) && Number.isFinite(o.quantity));
}

/** The last price of every spot pair, public and unsigned. Used to tell a
    holding from dust; the book values coins from its own marks. */
export async function fetchSpotPrices(signal?: AbortSignal): Promise<Map<string, number>> {
  const data = parse(await brokerGet({ url: `${SPOT}/api/v3/ticker/price`, ...(signal ? { signal } : {}) }));
  const rows = Array.isArray(data) ? data as Array<Record<string, unknown>> : [];
  const out = new Map<string, number>();
  for (const r of rows) {
    const price = num(r['price']);
    if (typeof r['symbol'] === 'string' && Number.isFinite(price)) out.set(r['symbol'], price);
  }
  return out;
}
